import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Scissors, 
  Layers, 
  TreeDeciduous, 
  Sprout,
  Ruler,
  Leaf,
  Phone,
  CheckCircle,
  Flower2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  scissors: Scissors,
  layers: Layers,
  "tree-deciduous": TreeDeciduous,
  sprout: Sprout,
  ruler: Ruler,
  leaf: Leaf,
  flower: Flower2,
};

export const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-heading text-gray-900 mb-4">
              Our <span className="text-primary">Services</span>
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Comprehensive landscaping and grounds care solutions tailored to your property's needs. 
              From routine maintenance to complete transformations.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Scissors;
              return (
                <Card 
                  key={service.id}
                  data-testid={`service-${service.id}`}
                  className="bg-white border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 group"
                >
                  <CardContent className="p-8">
                    <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-heading text-gray-900 mb-3">{service.name}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-accent font-accent text-lg">{service.price}</p>
                      <Link to="/contact">
                        <Button 
                          data-testid={`service-cta-${service.id}`}
                          size="sm"
                          className="bg-primary/10 hover:bg-primary text-primary hover:text-white font-accent"
                        >
                          Get Quote
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading text-gray-900 mb-4">
              How We <span className="text-accent">Work</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our streamlined process ensures you get the best service from start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Contact Us", description: "Reach out via phone, email, or our contact form to discuss your needs." },
              { step: "02", title: "Free Assessment", description: "We'll evaluate your property and provide a detailed quote." },
              { step: "03", title: "Schedule Service", description: "Choose a convenient time for your service appointment." },
              { step: "04", title: "Enjoy Results", description: "Sit back and enjoy your beautifully maintained property." },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-heading text-primary/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-heading text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section data-testid="service-areas" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-heading text-gray-900 mb-6">
                Areas We <span className="text-primary">Serve</span>
              </h2>
              <p className="text-gray-600 mb-8">
                We proudly serve residential and commercial properties throughout the Greater Houston area.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  "Houston", "Katy", "Sugar Land", "Pearland",
                  "Missouri City", "Richmond", "Cypress", "Spring",
                  "The Woodlands", "Humble", "Bellaire", "West University"
                ].map((area, index) => (
                  <div key={index} className="flex items-center text-gray-700">
                    <CheckCircle className="h-4 w-4 text-primary mr-2" />
                    {area}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-sm mt-6">
                Don't see your area? Contact us - we may still be able to help!
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-lg">
              <h3 className="text-2xl font-heading text-gray-900 mb-4">Ready to Get Started?</h3>
              <p className="text-gray-600 mb-6">
                Contact Damion today for a free property assessment and quote.
              </p>
              <div className="space-y-4">
                <a 
                  href="tel:7134999372" 
                  className="flex items-center justify-center w-full py-4 bg-primary text-white font-accent rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  (713) 499-9372
                </a>
                <Link to="/contact" className="block">
                  <Button 
                    data-testid="services-contact-btn"
                    className="w-full bg-accent hover:bg-accent/90 text-black font-accent"
                  >
                    Request a Quote Online
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
